import React, { FC, useEffect, useLayoutEffect } from 'react';
import { Outlet, useLocation } from 'react-router';
import { ROUTE_SOURCES } from 'src/constants/routeSources';
import { Wrapper } from 'src/templates/styled';
import {
  IUBENDA_EMBED_SCRIPT_ID,
  IUBENDA_EMBED_SCRIPT_SRC,
  teardownIubendaPublicConsentWidget,
} from 'src/utils/iubendaPublicConsentWidget';

/** Firma Clarity lato window (coda + `.q` prima dello script). */
type ClarityWindowFn = {
  // eslint-disable-next-line no-unused-vars -- nome richiesto dalla firma nel tipo oggetto
  (...args: unknown[]): void;
  q?: unknown[][];
};

declare global {
  interface Window {
    clarity?: ClarityWindowFn;
  }
}

const CLARITY_PROJECT_ID = 'wkwnyi3auw';
const CLARITY_SCRIPT_ID = 'ms-clarity-script';

function isBlogPath(pathname: string): boolean {
  const itBase = `/${ROUTE_SOURCES.BLOG_IT}`;
  return (
    pathname === `/${ROUTE_SOURCES.BLOG}` ||
    pathname.startsWith(`/${ROUTE_SOURCES.BLOG}/`) ||
    pathname === itBase ||
    pathname.startsWith(`${itBase}/`)
  );
}

function shouldLoadPublicIubendaWidget(pathname: string): boolean {
  return (
    pathname === '/' ||
    pathname.startsWith(`/${ROUTE_SOURCES.LANDING_ADV}`) ||
    pathname.startsWith(`/${ROUTE_SOURCES.LANDING_ADV_THANK_YOU}`) ||
    isBlogPath(pathname)
  );
}

export const PublicTemplate: FC = () => {
  const location = useLocation();
  const isProduction = process.env.NODE_ENV === 'production';
  const isReactSnap = navigator.userAgent === 'ReactSnap';

  useEffect(() => {
    if (!isProduction || isReactSnap) return;
    if (document.getElementById(CLARITY_SCRIPT_ID)) {
      return;
    }

    if (typeof window.clarity !== 'function') {
      const clarityQueueFn: ClarityWindowFn = (...args: unknown[]) => {
        clarityQueueFn.q = clarityQueueFn.q || [];
        clarityQueueFn.q.push(args);
      };
      window.clarity = clarityQueueFn;
    }

    const script = document.createElement('script');
    script.id = CLARITY_SCRIPT_ID;
    script.async = true;
    script.src = `https://www.clarity.ms/tag/${CLARITY_PROJECT_ID}`;
    document.head.appendChild(script);

    return () => {
      const script = document.getElementById(CLARITY_SCRIPT_ID);
      if (script?.parentNode) {
        script.parentNode.removeChild(script);
      }
      if (typeof window.clarity === 'function') {
        window.clarity('stop');
      }
    };
  }, [isProduction, isReactSnap]);

  useLayoutEffect(() => {
    if (!isProduction || isReactSnap) return;
    if (!shouldLoadPublicIubendaWidget(location.pathname)) {
      teardownIubendaPublicConsentWidget();
      return;
    }

    // Già presente: non fare teardown al cambio route (es. blog → home). Il cleanup
    // precedente chiamava sempre teardown e reiniettava lo script, con race su DOM/script
    // Iubenda cross-origin → overlay CRA "Script error." senza stack.
    if (document.getElementById(IUBENDA_EMBED_SCRIPT_ID)) {
      return;
    }

    const script = document.createElement('script');
    script.id = IUBENDA_EMBED_SCRIPT_ID;
    script.type = 'text/javascript';
    script.async = true;
    script.src = IUBENDA_EMBED_SCRIPT_SRC;
    document.head.appendChild(script);
  }, [isProduction, isReactSnap, location.pathname]);

  if (location.pathname.startsWith(`/${ROUTE_SOURCES.LANDING_ADV}`)) {
    return <Outlet />;
  }

  if (isBlogPath(location.pathname)) {
    return <Outlet />;
  }

  return (
    <Wrapper>
      <Outlet />
    </Wrapper>
  );
};
